import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import SubscriptionPlans from './SubscriptionPlans';
import { apiCall } from '../../utils/api';
import PaymentLoadingOverlay from './PaymentLoadingOverlay';

const SubscriptionModal = ({ isOpen, onClose, user, currentPlan, appId = 'saleswit', onSuccess }) => {
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState('');
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);  
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.keyCode === 27) onClose();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);
  
  // Prevent scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Clear error when modal opens
  useEffect(() => {
    if (isOpen) {
      setError('');
    }
  }, [isOpen]);

  // Check for cancellation and show error
  useEffect(() => {
    if (isOpen && currentPlan && isCancellationScheduled()) {
      setError("Your subscription is scheduled for cancellation or has been cancelled. Please contact support for immediate assistance if you want to resubscribe or wait until your current subscription expires.");
    }
  }, [isOpen, currentPlan]);

  // Helper function to check if current subscription is PayPal
  const isPayPalSubscription = () => {
    return currentPlan && currentPlan.paypal_subscription_id;
  };

  // Helper function to check if subscription is scheduled for cancellation
  const isCancellationScheduled = () => {
    if (!currentPlan) return false;
    return (
      (currentPlan.metadata && currentPlan.metadata.cancellation_scheduled === true) ||
      currentPlan.cancellation_scheduled === true ||
      (currentPlan.metadata && currentPlan.metadata.paypal_cancelled === true)
    );
  };

  // PayPal upgrade handler
  // PayPal upgrade handler
  const handlePayPalUpgrade = async (newPlan) => {
    if (!currentPlan || !currentPlan.id) {
      setError('Current subscription not found');
      return;
    }

    try {
      setShowLoadingOverlay(true);
      setUpgrading(true);
      setError('');

      // Call backend upgrade API
      const response = await apiCall('/api/subscriptions/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          subscription_id: currentPlan.id,
          new_plan_id: newPlan.id,
          app_id: appId,
          current_gateway: 'paypal'
        })
      });

      // ✅ ADD COMPREHENSIVE LOGGING HERE
     

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upgrade request failed');
      }

      const result = await response.json();

      // ✅ DETAILED RESULT LOGGING
      
      // Handle different upgrade scenarios
      if (result.result?.error) {
        // Handle specific error types
        if (result.result.error_type === 'discount_too_high') {
          setError("The remaining value is too high for automatic upgrade. Please contact support for assistance.");
          setShowLoadingOverlay(false);
          return;
        } else if (result.result.error_type === 'unsupported_currency') {
          setError("Unsupported currency. Please contact support.");
          setShowLoadingOverlay(false);
          return;
        }
        
        setError(result.result.message || 'Unable to process upgrade. This may be due to payment method restrictions or billing cycle timing. Please try again or contact support for assistance.');
        setShowLoadingOverlay(false);
        return;
      }

      // Check if it's a downgrade request
      if (result.result?.error_type === 'downgrade_requested') {
        setError(result.result.message);
        setShowLoadingOverlay(false);
        return;
      }

      // Handle PayPal proration payment required
      if (result.result?.payment_required && result.result?.payment_url) {
        // Close modal and redirect to PayPal for proration payment
        onClose();
        setTimeout(() => {
          window.location.href = result.result.payment_url;
        }, 500);
        return;
      }

      // Check if PayPal requires approval
      if (result.result?.approval_url) {
        // Close modal and redirect to PayPal for approval
        onClose();
        setTimeout(() => {
          window.location.href = result.result.approval_url;
        }, 500);
        return;
      } else {
      }

      // Handle successful upgrade with enhanced details
      if (result.result?.success) {
        // Enhanced success message handling
        let successMessage = result.result.message || 'Subscription upgraded successfully!';
        
        // Add payment method specific information
        if (result.result.payment_method) {
          if (result.result.upgrade_type === 'paypal_simple_immediate') {
              successMessage = `✨ PayPal upgrade successful! ${successMessage}`;
            } else if (result.result.upgrade_type === 'paypal_simple_with_approval') {
              successMessage = `✨ PayPal upgrade approved! ${successMessage}`;
            } else if (result.result.upgrade_type === 'paypal_with_proration') {
              successMessage = `💰 PayPal upgrade with proration completed! ${successMessage}`;
            }
        }        
        // Add discount information if present
        if (result.result.discount_applied) {
          successMessage += ` A ${result.result.discount_applied}% discount was applied for remaining value.`;
        }
        
        // Add proration information if present
        if (result.result.proration_amount) {
          successMessage += ` Proration amount: $${result.result.proration_amount.toFixed(2)}.`;
        }
        
        // Add temporary resources notice
        if (result.result.temporary_resources_added) {
          successMessage += ' Temporary resources have been added to your account until next billing cycle.';
        }
        setShowLoadingOverlay(false);
        onClose();
        
        // Trigger parent success handler with detailed message
        if (onSuccess) {
          onSuccess(successMessage);
        }
        
        // Refresh the page to update UI with longer delay for detailed message
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        
        return;
      }

      // ✅ LOG IF NONE OF THE CONDITIONS MATCHED
      

      // Success - close modal and let parent handle refresh
      onClose();
      
      // Optionally trigger a refresh in the parent component
      if (window.location.reload) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

    } catch (err) {
      console.error('PayPal upgrade error:', err);
      console.error('Error stack:', err.stack);
      setError(err.message || 'Failed to upgrade subscription. Please try again.');
    } finally {
      setUpgrading(false);
      setShowLoadingOverlay(false);
    }
  };
  // Razorpay upgrade handler (for consistency)
  const handleRazorpayUpgrade = async (newPlan) => {
    if (!currentPlan || !currentPlan.id) {
      setError('Current subscription not found');
      return;
    }

    try {
      setUpgrading(true);
      setError('');
      setShowLoadingOverlay(true);

      // Call backend upgrade API
      const response = await apiCall('/api/subscriptions/upgrade', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user.uid,
          subscription_id: currentPlan.id,
          new_plan_id: newPlan.id,
          app_id: appId,
          current_gateway: 'razorpay'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle UPI limitation specifically
        if (response.status === 422 && errorData.error_type === 'upi_upgrade_not_supported') {
          setError("Since your subscription is set-up on UPI payments, you need to cancel the subscription and get the new subscription. Or you can contact us using the contact form link at bottom of page.");
          setShowLoadingOverlay(true);
          return;
        }
        
        throw new Error(errorData.error || 'Upgrade request failed');
      }

      const result = await response.json();

      // Handle different upgrade scenarios
      if (result.result?.error) {
        // Handle specific error types
        if (result.result.error_type === 'discount_too_high') {
          setError("The remaining value is too high for automatic upgrade. Please contact support for assistance.");
          setShowLoadingOverlay(false);
          return;
        } else if (result.result.error_type === 'unsupported_currency') {
          setError("Unsupported currency. Please contact support.");
          setShowLoadingOverlay(false);
          return;
        } else if (result.result.error_type === 'upi_upgrade_not_supported') {
          setError("Since your subscription is set-up on UPI payments, you need to cancel the subscription and get the new subscription. Or you can contact us using the contact form link at bottom of page.");
          setShowLoadingOverlay(false);
          return;
        }
        
        setError(result.result.message || 'Unable to process upgrade. This may be due to payment method restrictions or billing cycle timing. Please try again or contact support for assistance.');
        setShowLoadingOverlay(false);
        return;
      }

      // Check if it's a downgrade request
      if (result.result?.error_type === 'downgrade_requested') {
        setError(result.result.message);
        setShowLoadingOverlay(false);
        return;
      }

      // Handle successful upgrade with enhanced details
      if (result.result?.success) {
        // Enhanced success message handling
        let successMessage = result.result.message || 'Subscription upgraded successfully!';
        
        // Add payment method specific information
        if (result.result.payment_method) {
          if (result.result.upgrade_type === 'cancel_and_recreate_with_discount') {
            successMessage = `✨ Your ${result.result.payment_method.toUpperCase()} payment method qualified for automatic discount! ${successMessage}`;
          } else if (result.result.upgrade_type === 'cancel_and_recreate_with_refund') {
            successMessage = `💰 Upgrade completed with refund processing! ${successMessage} You'll receive a refund of ₹${result.result.refund_amount.toFixed(2)} within 3-5 working days.`;
          } else if (result.result.upgrade_type === 'razorpay_plan_change') {
            successMessage = `⚡ Razorpay upgrade completed instantly! ${successMessage}`;
          }
        }
        
        // Add discount information
        if (result.result.discount_applied) {
          successMessage += ` A ${result.result.discount_applied}% discount was applied for remaining value.`;
        }
        
        // Add refund information
        if (result.result.refund_amount && result.result.upgrade_type === 'cancel_and_recreate_with_refund') {
          successMessage += ` Refund of ₹${result.result.refund_amount.toFixed(2)} will be processed within 3-5 working days.`;
        }
        
        // Add temporary resources information
        if (result.result.temporary_resources_added) {
          successMessage += ' Temporary resources have been added to your account until next billing cycle.';
        }
        
        // Add additional payment information
        if (result.result.additional_payment_required) {
          successMessage += ` Additional payment of $${result.result.additional_amount.toFixed(2)} required for excess resource usage.`;
          
          // Handle additional payment link
          if (result.result.additional_payment_link) {
          onClose();
          setTimeout(() => {
            window.location.href = result.result.additional_payment_link;
          }, 500);
          return;
          }
        }
        
        // Handle new subscription link for INR upgrades
        if (result.result.razorpay_link) {
        onClose();
        setTimeout(() => {
          window.location.href = result.result.razorpay_link;
        }, 500);
        return;
        }
        setShowLoadingOverlay(false);
        onClose();
        
        // Trigger parent success handler with enhanced message
        if (onSuccess) {
          onSuccess(successMessage);
        }
        
        // Refresh the page to update UI with longer delay for detailed message
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }

      // Success - close modal and let parent handle refresh
      onClose();
      
      // Optionally trigger a refresh in the parent component
      if (window.location.reload) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

    } catch (err) {
      console.error('Razorpay upgrade error:', err);
      setShowLoadingOverlay(false);
      setError(err.message || 'Failed to upgrade subscription. Please try again.');
    } finally {
      setUpgrading(false);
      setShowLoadingOverlay(false);

    }
  };

  // Universal upgrade handler that detects payment method
  const handleUpgrade = async (newPlan) => {
    if (isPayPalSubscription()) {
      await handlePayPalUpgrade(newPlan);
    } else {
      await handleRazorpayUpgrade(newPlan);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30 overflow-y-auto">
      <div className="relative bg-[#0c1526] rounded-xl w-full max-w-7xl mx-4 my-8 max-h-[90vh] overflow-y-auto shadow-xl border border-gray-700">
        <div className="sticky top-0 z-10 flex justify-between items-center p-4 border-b border-gray-700 bg-[#0c1526]">
          <div>
            <h2 className="text-xl font-bold text-white">
              {appId === 'saleswit' ? 'saleswit' : 'SalesWit'} Subscription Plans
            </h2>
            {currentPlan && (
              <p className="text-sm text-gray-400 mt-1">
                Current: {currentPlan.plan_name || currentPlan.name}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-[#273349] text-gray-400 hover:text-white transition-colors"
            disabled={upgrading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg">
            <p className="text-red-300 text-sm">{error}</p>
          </div>
        )}

        {/* Upgrading Message */}
        {upgrading && (
          <div className="mx-4 mt-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
            <p className="text-blue-300 text-sm flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-300" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {isPayPalSubscription() 
                ? 'Processing PayPal upgrade...' 
                : 'Processing Razorpay upgrade...'
              }
            </p>
          </div>
        )}
        
        <SubscriptionPlans 
          user={user} 
          onClose={onClose} 
          currentPlan={currentPlan} 
          appId={appId}
          // Pass the upgrade handler as a prop
          onUpgrade={handleUpgrade}
          // Pass upgrading state to disable buttons
          isUpgrading={upgrading}
          // Pass PayPal detection function
          isPayPalSubscription={isPayPalSubscription}
        />
        <PaymentLoadingOverlay 
        isVisible={showLoadingOverlay} 
        message="Processing your upgrade request..."
        />
      </div>
    </div>
  );
};

export default SubscriptionModal;